import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@niutraan/hooks/src/lib/api";
import type {
  CreateInstitutionLocationDto,
  InstitutionLocationDto,
  UpdateInstitutionLocationDto,
} from "@niutraan/shared-dto";
import { useInstitutionLocationStore } from "@/stores/useInstitutionLocationStore";

// Query keys
const QUERY_KEYS = {
  institutionLocations: "institutionLocations",
  activeInstitutionLocations: "activeInstitutionLocations",
};

// Get all institution locations
export const useInstitutionLocations = () => {
  const setLocations = useInstitutionLocationStore(
    (state: any) => state.setLocations,
  );

  return useQuery({
    queryKey: [QUERY_KEYS.institutionLocations],
    queryFn: async () => {
      const data = await apiClient.get<InstitutionLocationDto[]>(
        "/institution-locations",
      );
      setLocations(data);
      return data;
    },
  });
};

// Get active institution locations
export const useActiveInstitutionLocations = () => {
  const setActiveLocations = useInstitutionLocationStore(
    (state: any) => state.setActiveLocations,
  );

  return useQuery({
    queryKey: [QUERY_KEYS.activeInstitutionLocations],
    queryFn: async () => {
      const data = await apiClient.get<InstitutionLocationDto[]>(
        "/institution-locations/active",
      );
      setActiveLocations(data);
      return data;
    },
  });
};

// Get institution location by ID
export const useInstitutionLocationById = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.institutionLocations, id],
    queryFn: () =>
      apiClient.get<InstitutionLocationDto>(`/institution-locations/${id}`),
    enabled: !!id,
  });
};

// Create institution location
export const useCreateInstitutionLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInstitutionLocationDto) =>
      apiClient.post<InstitutionLocationDto>("/institution-locations", data),
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.institutionLocations],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.activeInstitutionLocations],
      });
    },
  });
};

// Update institution location
export const useUpdateInstitutionLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateInstitutionLocationDto;
    }) =>
      apiClient.put<InstitutionLocationDto>(
        `/institution-locations/${id}`,
        data,
      ),
    onSuccess: (data) => {
      // Update cache directly
      queryClient.setQueryData(
        [QUERY_KEYS.institutionLocations, data.id],
        data,
      );
      // Invalidate lists to refetch
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.institutionLocations],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.activeInstitutionLocations],
      });
    },
  });
};

// Delete institution location
export const useDeleteInstitutionLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<InstitutionLocationDto>(`/institution-locations/${id}`),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.institutionLocations, id],
      });
      // Invalidate lists to refetch
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.institutionLocations],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.activeInstitutionLocations],
      });
    },
  });
};
